import { useState, useCallback } from "react";
import type { File } from "@tellescope/types-models";
import { useEnduserStore } from "../store/hooks";

interface UseFilePreviewOptions {
  /** Called when preview loading starts */
  onStart?: () => void;
  /** Called when preview URL is ready */
  onSuccess?: (url: string) => void;
  /** Called when preview loading fails */
  onError?: (error: Error) => void;
}

interface UseFilePreviewReturn {
  /** Load a preview URL for a file */
  loadPreview: (file: File) => Promise<string | null>;
  /** The currently loaded preview URL */
  previewUrl: string | null;
  /** The file currently being previewed */
  previewFile: File | null;
  /** Whether a preview is currently loading */
  isLoading: boolean;
  /** Error from the last preview attempt */
  error: Error | null;
  /** Clear the current preview */
  clearPreview: () => void;
}

/** File types that support preview */
const PREVIEWABLE_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
const PREVIEWABLE_PDF_TYPE = "application/pdf";

/**
 * Check if a file type supports preview
 */
export function isPreviewable(mimeType: string | undefined): boolean {
  if (!mimeType) return false;
  return PREVIEWABLE_IMAGE_TYPES.includes(mimeType) || mimeType === PREVIEWABLE_PDF_TYPE;
}

/**
 * Check if a file type is an image
 */
export function isImageType(mimeType: string): boolean {
  return PREVIEWABLE_IMAGE_TYPES.includes(mimeType);
}

/**
 * Check if a file type is a PDF
 */
export function isPdfType(mimeType: string): boolean {
  return mimeType === PREVIEWABLE_PDF_TYPE;
}

/**
 * Hook for loading file preview URLs using the Tellescope SDK.
 *
 * Uses the `file_download_URL` API to get a presigned URL that can be used
 * for displaying images or PDFs in a preview modal.
 *
 * @example
 * ```tsx
 * const { loadPreview, previewUrl, previewFile, isLoading, clearPreview } = useFilePreview();
 *
 * const handlePreview = async (file: File) => {
 *   if (isPreviewable(file.type)) {
 *     await loadPreview(file);
 *   }
 * };
 *
 * return (
 *   <>
 *     <Button onClick={() => handlePreview(file)} disabled={isLoading}>
 *       Preview
 *     </Button>
 *     {previewUrl && previewFile && (
 *       <FilePreviewDialog
 *         file={previewFile}
 *         url={previewUrl}
 *         onClose={clearPreview}
 *       />
 *     )}
 *   </>
 * );
 * ```
 */
export function useFilePreview(options: UseFilePreviewOptions = {}): UseFilePreviewReturn {
  const { onStart, onSuccess, onError } = options;
  const session = useEnduserStore((state) => state.session);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  const loadPreview = useCallback(
    async (file: File): Promise<string | null> => {
      if (!session) {
        const err = new Error("No active session");
        setError(err);
        onError?.(err);
        return null;
      }

      if (!file.secureName) {
        const err = new Error("File has no secureName - cannot preview");
        setError(err);
        onError?.(err);
        return null;
      }

      if (!isPreviewable(file.type)) {
        const err = new Error(`File type ${file.type} is not previewable`);
        setError(err);
        onError?.(err);
        return null;
      }

      setIsLoading(true);
      setError(null);
      onStart?.();

      try {
        // Get the presigned download URL from the API
        const { downloadURL } = await session.api.files.file_download_URL({
          secureName: file.secureName,
        });

        setPreviewUrl(downloadURL);
        setPreviewFile(file);
        onSuccess?.(downloadURL);
        return downloadURL;
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to load preview");
        setError(error);
        onError?.(error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [session, onStart, onSuccess, onError]
  );

  const clearPreview = useCallback(() => {
    setPreviewUrl(null);
    setPreviewFile(null);
    setError(null);
  }, []);

  return {
    loadPreview,
    previewUrl,
    previewFile,
    isLoading,
    error,
    clearPreview,
  };
}
